/**
 * CelebrationProvider — the ONE shared "success moment" surface (Jose 2026-08-06).
 *
 * Fires a celebratory micro-animation (confetti / cannons / fireworks / stars — picked at RANDOM
 * each time so it never gets monotonous) on any key completion, and optionally shows a little
 * success card. Two ways to use it:
 *
 *   1. celebrateConfetti(kind?)  — module-level, no React. Just the burst. Use on surfaces that
 *      ALREADY have their own confirmation UI (e.g. the "Booking Confirmed!" modal) — add sparkle
 *      without a second card.
 *   2. useCelebration().celebrate({...})  — shows a success CARD (auto-dismissing 'pop' for pure
 *      awareness, or a 'modal' with detail rows + action buttons) AND fires the confetti.
 *
 * Mount <CelebrationProvider> once near the app root.
 */
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import confetti from 'canvas-confetti';

type ConfettiKind = 'burst' | 'cannons' | 'fireworks' | 'stars';
const KINDS: ConfettiKind[] = ['burst', 'cannons', 'fireworks', 'stars'];

/** Fire ONE celebration animation. Omit `kind` for a random pick (keeps it fresh, not monotonous). */
export function celebrateConfetti(kind?: ConfettiKind) {
  const pick = kind || KINDS[Math.floor(Math.random() * KINDS.length)];
  const colors = ['#8b5cf6', '#6366f1', '#22c55e', '#f59e0b', '#ec4899', '#06b6d4'];
  try {
    if (pick === 'cannons') {
      confetti({ particleCount: 90, spread: 65, angle: 60, origin: { x: 0, y: 0.7 }, colors });
      confetti({ particleCount: 90, spread: 65, angle: 120, origin: { x: 1, y: 0.7 }, colors });
    } else if (pick === 'fireworks') {
      const end = Date.now() + 900;
      (function frame() {
        confetti({ particleCount: 7, angle: 60, spread: 55, startVelocity: 45, origin: { x: 0, y: 0.7 }, colors });
        confetti({ particleCount: 7, angle: 120, spread: 55, startVelocity: 45, origin: { x: 1, y: 0.7 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
      })();
    } else if (pick === 'stars') {
      confetti({ particleCount: 70, spread: 110, scalar: 1.2, shapes: ['star'], origin: { y: 0.55 }, colors });
    } else {
      confetti({ particleCount: 140, spread: 75, startVelocity: 42, origin: { y: 0.6 }, colors });
    }
  } catch { /* confetti is cosmetic — never let it break a flow */ }
}

export interface CelebrateOptions {
  title: string;
  subtitle?: string;
  rows?: { label: string; value: string }[];
  mode?: 'pop' | 'modal';           // 'pop' = compact + auto-dismiss (awareness); 'modal' = card + buttons
  autoDismissMs?: number;           // for 'pop' (default 2600)
  actions?: { label: string; onClick?: () => void; primary?: boolean }[];
  // Small clickable link in the TOP-RIGHT corner of a modal card (Jose 2026-08-10). Used so the
  // checkout-complete card can jump straight to Rebook without reopening the appointment card.
  topRightAction?: { label: string; onClick: () => void };
  // Arbitrary content rendered inside a modal card, between the rows and the action buttons (Jose
  // 2026-08-19). Receives `close` so the content can dismiss the card (e.g. the checkout receipt-actions
  // row: Email/Text/Print/Void). Keeps CelebrationProvider generic while the receipt logic lives in its
  // own component.
  renderContent?: (close: () => void) => React.ReactNode;
  animation?: ConfettiKind;         // force a specific animation; default random
}

interface CelebrationContextValue {
  celebrate: (opts: CelebrateOptions) => void;
}
const CelebrationContext = createContext<CelebrationContextValue>({ celebrate: () => {} });

export const useCelebration = () => useContext(CelebrationContext);

export const CelebrationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [active, setActive] = useState<CelebrateOptions | null>(null);
  const timerRef = useRef<any>(null);

  const close = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setActive(null);
  }, []);

  const celebrate = useCallback((opts: CelebrateOptions) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    celebrateConfetti(opts.animation);
    setActive(opts);
    const mode = opts.mode || 'pop';
    // 'pop' always auto-dismisses (default 2.6s). 'modal' stays open UNLESS the caller sets autoDismissMs
    // — the checkout-complete modal passes 30000 so it clears itself off the station (Jose 2026-10-22).
    const dismissMs = mode === 'pop' ? (opts.autoDismissMs ?? 2600) : opts.autoDismissMs;
    if (dismissMs && dismissMs > 0) {
      timerRef.current = setTimeout(() => setActive(null), dismissMs);
    }
  }, []);

  const mode = active?.mode || 'pop';

  return (
    <CelebrationContext.Provider value={{ celebrate }}>
      {children}
      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
            style={{ background: mode === 'modal' ? 'rgba(0,0,0,0.5)' : 'transparent', pointerEvents: mode === 'modal' ? 'auto' : 'none' }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={mode === 'modal' ? close : undefined}
          >
            <motion.div
              className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl px-8 py-7 text-center max-w-sm w-full"
              style={{ pointerEvents: 'auto' }}
              initial={{ scale: 0.6, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.7, opacity: 0, y: 10 }}
              transition={{ type: 'spring', stiffness: 320, damping: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Top-right clickable link (Jose 2026-08-10) — e.g. "Rebook →" on the checkout card. */}
              {mode === 'modal' && active.topRightAction && (
                <button
                  onClick={() => { active.topRightAction!.onClick(); close(); }}
                  className="absolute top-3 right-3 px-3 py-1.5 rounded-full text-sm font-bold text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/40 hover:bg-purple-200 dark:hover:bg-purple-900/70 transition-colors"
                >
                  {active.topRightAction.label}
                </button>
              )}
              {/* ✕ close (modal, when there's no top-right pill) — "tap ✕ to just close" (Jose 2026-08-19). */}
              {mode === 'modal' && !active.topRightAction && (
                <button
                  onClick={close}
                  aria-label="Close"
                  className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
              <motion.div
                className="mx-auto mb-4 w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center"
                initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400, damping: 12, delay: 0.05 }}
              >
                <svg className="w-9 h-9 text-green-500" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{active.title}</h3>
              {active.subtitle && <p className="text-sm text-gray-500 dark:text-gray-300 mt-1">{active.subtitle}</p>}

              {active.rows && active.rows.length > 0 && (
                <div className="mt-4 bg-gray-50 dark:bg-gray-900/40 rounded-xl p-4 text-left space-y-1.5">
                  {active.rows.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">{r.label}</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{r.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Card content slot (modal) — e.g. the checkout receipt-actions row (Jose 2026-08-19). */}
              {mode === 'modal' && active.renderContent && active.renderContent(close)}

              {mode === 'modal' && active.actions && active.actions.length > 0 && (
                <div className="mt-5 space-y-2">
                  {active.actions.map((a, i) => (
                    <button
                      key={i}
                      onClick={() => { a.onClick?.(); close(); }}
                      className={`w-full py-3 rounded-xl font-semibold transition-colors ${
                        a.primary
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </CelebrationContext.Provider>
  );
};

export default CelebrationProvider;
